import * as React from "react";
import * as _ from "lodash";
import Modal from "../../../../misc/components/modal";
import Alert, { AlertCategoryEnum } from "../../../../misc/components/Alert";
import { AuditModalBody } from "../../../../auditmodal/components/AuditModalBody";
import {
    CoughReOpenComments,
    ModelWithError,
    CoughReOpenCommentGroup,
    isModelWithError
} from "../../../../misc/api/apiTypes";
import { AuditModalReOpenComments } from "./DropdownComments";
import { IsFreeTextAllowed } from "./DropdownCommentsHelpers";
import { FontSize } from "../../../../misc/components/loading";
import LoadingWithMessage from "../../../../misc/components/LoadingWithMessage";
import { useWebPortalLegacyClient } from "../../../../misc/api/WebPortalTokenClient";

export interface BaseDropdownModalProps {
    onClose: () => void;
}

interface DropdownCommentsModalProps extends BaseDropdownModalProps {
    editMode: boolean;
    saveFixedComments: (
        selectedFixedComments: CoughReOpenCommentGroup[],
        auditAnnotation: string
    ) => Promise<void | ModelWithError>;
    fetchFixedCommentsForAnalysisSet: () => Promise<CoughReOpenComments | ModelWithError>;
    selectedCommentsValidator: (
        selectedFixedComments: CoughReOpenCommentGroup[],
        auditAnnotation
    ) => string[];
    showLinkedWarning: boolean;
    header: string;
    show: boolean;
    loading: boolean;
}

const DropdownCommentsModal = ({
    editMode,
    onClose,
    showLinkedWarning,
    header,
    saveFixedComments,
    fetchFixedCommentsForAnalysisSet,
    show,
    selectedCommentsValidator,
    loading
}: DropdownCommentsModalProps): JSX.Element => {
    const [error, setErrors] = React.useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [fetchingComments, setFetchingComments] = React.useState<boolean>(false);
    const [availableComments, setAvailableComments] = React.useState<CoughReOpenComments>();
    const [selectedFixedComments, setSelectedFixedComments] = React.useState<
        CoughReOpenCommentGroup[]
        >([]);
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        setFetchingComments(true);
        legacyClient.getReOpenAnalysisSetFixedComments()
            .then(comments => {
                setAvailableComments(comments);
            })
            .catch((err: ModelWithError) => {
                setErrors(err.Errors?.map(e => e.Message));
            })
            .finally(() => {
                setFetchingComments(false);
            });
    }, []);

    React.useEffect(() => {
        if (!availableComments) return;

        if (!editMode) {
            setSelectedFixedComments(
                availableComments.ReOpenCommentGroups.map(cG => {
                    return {
                        ...cG,
                        SelectedOption: null
                    };
                })
            );
            return;
        }

        setFetchingComments(true);

        fetchFixedCommentsForAnalysisSet()
            .then((comments: CoughReOpenComments) => {
                setSelectedFixedComments(comments.ReOpenCommentGroups);
            })
            .catch((err: ModelWithError) => {
                setErrors(err.Errors?.map(e => e.Message));
            })
            .finally(() => {
                setFetchingComments(false);
            });
    }, [availableComments, editMode]);

    const handleFixedCommentChange = (groupIndex: number, selectedComment: string) => {
        setSelectedFixedComments(fg => {
            return fg.map(fcG => {
                if (fcG.Order === groupIndex) {
                    return {
                        ...fcG,
                        SelectedOption: selectedComment
                    };
                }

                return fcG;
            });
        });
    };

    const onSubmit = (auditAnnotation: string, selected: CoughReOpenCommentGroup[]) => {
        setErrors([]);
        const errors = selectedCommentsValidator(selected, auditAnnotation);

        if (errors.length) {
            setErrors(errors);
            return;
        }

        setIsSubmitting(true);

        saveFixedComments(selected, auditAnnotation)
            .then(json => {
                if (isModelWithError(json)) {
                    setErrors(json.Errors?.map(err => err.Message));
                    return;
                }

                onClose();
            })
            .catch((err: Error) => {
                setErrors([err.message]);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <Modal show={show} closeModalHandler={onClose} header={header}>
            <Alert show={showLinkedWarning} category={AlertCategoryEnum.Warning}>
                <b>WARNING:</b> This analysis set has linked analysis sets. Reopening this analysis
                set will cause any linked analysis sets to be reopened also.
            </Alert>
            <Alert show={!!_.first(error)} category={AlertCategoryEnum.Danger}>
                <ul>
                    {_.map(error, (err, index) => (
                        <li key={index}>{err}</li>
                    ))}
                </ul>
            </Alert>
            {loading ? (
                <LoadingWithMessage message={"Loading"} size={FontSize.FourX} />
            ) : (
                <AuditModalBody
                    isAuditAnnotationDisabled={
                        !editMode && !IsFreeTextAllowed(selectedFixedComments)
                    }
                    isSubmitting={isSubmitting}
                    onSubmit={form => onSubmit(form.Annotation, selectedFixedComments)}
                    onClose={onClose}
                    annotationLabel="Free Text Comments"
                >
                    <AuditModalReOpenComments
                        fetchingComments={fetchingComments}
                        fixedComments={{
                            ReOpenCommentGroups: selectedFixedComments
                        }}
                        onFixedCommentToggle={handleFixedCommentChange}
                    />
                </AuditModalBody>
            )}
        </Modal>
    );
};

export default DropdownCommentsModal;
